<template>
  <main class="arrivals-page">
    <div class="arrivals-page__body" ref="body">
      <div class="arrivals-page__row">
        <h1 class="arrivals-page__title">Поступления товаров</h1>
        <div class="arrivals-page__buttons">
          <button
            class="arrivals-page__buttons-add"
            type="button"
            v-on:click="openArrivalDialogForm">
            Добавить поступление
          </button>
        </div>
      </div>
      <arrival-table
        v-bind:arrival-list="arrivalList"
        v-bind:is-await-request="isAwaitRequest"
        v-on:delete-arrival="onDeleteArrival"
        v-on:edit-arrival="onRouteToArrival" />
      <the-pagination v-model="page" v-bind:page-count="pageCount" />
    </div>
    <side-slide-transition>
      <confirm-side-dialog
        class="arrivals-page__side-dialog"
        label='Добавить поступление'
        v-if="showArrivalDialogForm"
        v-on:cancel="closeArrivalDialogForm">
        <form-arrival-products
          v-bind:vendor-options="vendors"
          v-bind:product-options="products"
          v-bind:input-errors="inputErrors"
          v-bind:new-product-id="newProductId"
          v-on:create-new-product="openProductDialogForm"
          v-on:submit="onSubmitArrival"
          v-on:change="clearErrors" />
      </confirm-side-dialog>
    </side-slide-transition>
    <modal-dialog
      title="Добавить новый товар"
      content-class="overflow-visible"
      width="100%"
      max-width="450px"
      v-bind:value="shownProductDialogForm">
      <form-product
        v-bind:has-create-unit="false"
        v-bind:category-options="categories"
        v-bind:unit-options="units"
        v-bind:vendor-options="vendors"
        v-bind:initial-name="product.name"
        v-bind:initial-category="product.category"
        v-bind:initial-manufacturer="product.manufacturer"
        v-bind:initial-unit="product.unit"
        v-bind:initial-vendor="product.vendor"
        v-bind:initial-count-in-pack="product.countInPack"
        v-bind:input-errors="inputErrors"
        v-on:submit="createProduct"
        v-on:create-category="openCategoryForm"
        v-on:close="closeProductDialogForm" />
    </modal-dialog>
    <modal-dialog
      content-class="products-page__modal"
      title="Создать категорию"
      v-model="showProductCategoryDialogForm"
      v-on:input="closeProductCategoryDialogForm"
      with-cross
    >
      <form-product-category
        v-bind:category-id="category.id"
        v-bind:initial-name="category.name"
        v-bind:input-errors="inputErrors"
        v-on:submit="createProductCategory"
        v-on:close="closeProductCategoryDialogForm" />
    </modal-dialog>
  </main>
</template>

<script>
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import ThePagination from '@/components/ThePagination.vue';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import ArrivalTable from '@/components/Warehouse/ArrivalTable.vue';
import FormArrivalProducts from '@/components/Warehouse/FormArrivalProducts.vue';
import notifierMixin from '@/mixins/notifierMixin';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import { Product, ProductCategory } from '@/storage/gateways/ProductGateway';
import ModalDialog from '@/components/ModalDialog.vue';
import FormProduct from '@/components/Warehouse/FormProduct.vue';
import FormProductCategory from '@/components/Warehouse/FormProductCategory.vue';

/**
 * @description Примесь товара.
 * @type {Vue}
 */
const productMixin = {
  data() {
    return {
      product: new Product(),
      shownProductDialogForm: false,
      newProductId: null,
    };
  },
  methods: {
    openProductDialogForm() {
      this.product = new Product();
      this.shownProductDialogForm = true;
      this.inputErrors = {};
    },
    closeProductDialogForm() {
      this.shownProductDialogForm = false;
      this.product = new Product();
    },
    /**
     * @description Создать товар.
     * @param {ProductRequest} request
     */
    createProduct(request) {
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.fetchOptions().finally(() => {
          this.newProductId = response.product_id;
          this.closeProductDialogForm();
        });
        this.inputErrors = {};
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$productGateway
        .create(request.toQuery())
        .then((result) => result.fold(onFail, onSuccess));
    },
  },
};

/**
 * @description Примесь категорий товаров.
 * @type {any}
 */
const categoryMixin = {
  formCategory: 'category',
  data() {
    return {
      category: new ProductCategory(),
      showProductCategoryDialogForm: false,
    };
  },
  methods: {
    /**
     * @description Обработать выбор категорий.
     * @param {array} categories
     */
    onSelectingCategory(categories) {
      this.selectedCategories = categories;
      this.fetchProductList(this.sort, this.selectedCategories);
    },
    /**
     * @description Открыть форму для работы с категорией.
     * @returns {void}
     */
    openCategoryForm() {
      this.category = new ProductCategory();
      this.showProductCategoryDialogForm = true;
    },
    /**
     * @description Закрыть форму категории.
     * @returns {void}
     */
    closeProductCategoryDialogForm() {
      this.showProductCategoryDialogForm = false;
    },
    /**
     * @description Создать категорию товара.
     * @param {ProductCategoryRequest} request
     */
    createProductCategory(request) {
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.inputErrors = {};
        this.closeProductCategoryDialogForm();
        this.fetchOptions().then(() => {
          this.product.category = response.category_id;
        });
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$productGateway
        .createProductCategory(request.toQuery())
        .then((result) => result.fold(onFail, onSuccess));
    },
  },
};

/**
 * @description Страница списка поступления товаров.
 * @type {Vue}
 */
export default {
  name: 'ClinicPanelWarehouseArrivalList',
  components: {
    FormProductCategory,
    FormProduct,
    ModalDialog,
    FormArrivalProducts,
    ConfirmSideDialog,
    ArrivalTable,
    ThePagination,
    SideSlideTransition,
  },
  created() {
    this.fetchOptions();
    this.fetchArrivalList();
  },
  data() {
    return {
      arrivalList: [],
      isAwaitRequest: false,
      page: 1,
      pageCount: 0,
      inputErrors: {},
      showArrivalDialogForm: false,
      isAwaitDownloadRequest: false,
      vendors: [],
      products: [],
      categories: [],
      units: [],
    };
  },
  methods: {
    /**
     * @description Очистить ошибки.
     */
    clearErrors() {
      this.inputErrors = {};
    },
    /**
     * @description Создать поступления.
     * @param {ArrivalRequest} request
     */
    createArrival(request) {
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.closeArrivalDialogForm();
        this.inputErrors = {};
        this.fetchArrivalList();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$arrivalGateway
        .create(request.toQuery())
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Закрыть диалоговое окно формы поступленияа.
     * @returns {void}
     */
    closeArrivalDialogForm() {
      this.showArrivalDialogForm = false;
    },
    /**
     * @description Открыть диалоговое окно формы поступленияов.
     * @returns {void}
     */
    openArrivalDialogForm() {
      this.showArrivalDialogForm = true;
      this.inputErrors = {};
    },
    /**
     * @description Получить список поступлений.
     * @returns {Promise<void>}
     */
    async fetchArrivalList() {
      const onSuccess = ({ arrivals, pageCount }) => {
        this.arrivalList = arrivals;
        this.pageCount = pageCount;
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this.isAwaitRequest = true;
      const either = await this.$arrivalGateway.getAll(this.page);
      either.fold(onFail, onSuccess);
      this.isAwaitRequest = false;
    },
    /**
     * @description Получить опции.
     * @returns {Promise}
     */
    fetchOptions() {
      const onSuccess = (
        {
          vendors,
          products,
          units,
          categories,
        },
      ) => {
        this.vendors = vendors;
        this.products = products;
        this.units = units;
        this.categories = categories;
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      return this
        .$arrivalGateway
        .viewOptions()
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать отправку запроса поступления.
     * @param {ArrivalRequest} request
     */
    onSubmitArrival(request) {
      this.createArrival(request);
    },
    /**
     * @description Обработать событие удаления поступления.
     * @param {number} id Идентификатор поступления.
     * @returns {void}
     */
    async onDeleteArrival(id) {
      const dialog = await this
        .showRemoveDialog('Вы действительно хотите безвозвратно удалить поступление?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.fetchArrivalList();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$arrivalGateway
        .delete(id)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Переход на страницу поступления.
     * @param {string} id
     * @returns {Promise<void>}
     */
    async onRouteToArrival(id) {
      await this.$router.push({
        name: 'ClinicPanelWarehouseProductArrival',
        params: {
          arrivalId: id,
        },
      });
    },
  },
  mixins: [
    notifierMixin,
    builtInDialogMixin,
    productMixin,
    categoryMixin,
  ],
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.v-snack__content {
  white-space: pre-line;
}

.v-card__title {
  margin-bottom: 10px;
  word-break: auto-phrase;
}

.v-card__text {
  display: grid;
  gap: 15px;
  &__description {
    font-size: 16px;
  }
}

.arrivals {
  &-page {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    &__filters {
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 20px;
      &-label {
        color: gray;
        font-size: 12px;
      }
      &-additional {
        display: flex;
        gap:10px;
        margin-top: 20px;
      }
    }
    &__categories {
      width: 280px;
      &__caption {
        font-size: 14px;
        line-height: 17px;
        font-weight: 700;
        border-bottom: 2px solid #0489e0;
        padding: 10px;
      }
      &__container {
        display: flex;
        flex-direction: column;
        gap: 15px;
        width: 280px;
        background: #f2f7fa;
        padding: 20px 10px;
      }
    }
    &__buttons {
      display: grid;
      grid-template-columns: 1fr;
      max-width: 245px;
      width: 100%;
      column-gap: 10px;
      align-items: center;
      &-filter {
        margin-top: 25px;
        display: flex;
        align-items: center;
        gap: 5px;
        max-width: 175px;
        width: 100%;
        color: #1F85C8;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        >img {
          width: 15px;
          height: 15px;
        }
      }
      &-add {
        @include buttons.btn-blue(100%, 48px, 16px);
        @include buttons.btn-blue-add(19px);
      }

      &-export {
        @include buttons.btn-transparent(100%, 48px, 16px);

        &:before {
          content: "";
          background-image: url("@/assets/img/download-file-blue.svg");
          background-repeat: no-repeat;
          background-size: contain;
          width: 20px;
          height: 20px;
          margin: 0 8px 0 0;
        }

        &:disabled {
          color: var(--unfocus-color);
          border-color: var(--unfocus-color);
          background-color: transparent;
        }
      }

      &-delete {
        @include buttons.btn-blue(50%, 48px, 16px);
        width: 100%;
      }

      &-delete:disabled {
        background-color: var(--unfocus-color);
      }
    }

    &__body {
      padding: 38px 20px 48px 23px;
      width: 100%;
      overflow-y: scroll;
    }

    &__row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }

    &__search {
      max-width: 400px;
    }
    &__side-dialog {
      .side-dialog {
        padding-bottom: 250px;
      }
    }
  }
}

@media screen and (max-width: 960px) {
  .arrivals {
    &-page {
      flex-direction: column;
      &__row {
        flex-direction: column;
        gap: 15px;
      }
    }
  }
}

@media screen and (max-width: 640px) {
  .arrivals {
    &-page {
      &__body {
        padding: 38px 10px 140px;
        width: 100%;
        overflow-y: scroll;
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .v-text-field__slot input::placeholder {
    font-size: 12px;
  }
  .arrivals-page__buttons-export {
    display: none;
  }
}

@media screen and (max-width: 400px) {
  .v-card__actions {
    .dialog__button {
      width: 100%;
    }
  }
}
</style>
